import React, { useState } from 'react';
import { emailService } from './utils/emailService';

const resources = [
  {
    title: '5 Common Financial Mistakes Promoters Make When Securing Funding for Hospitals',
    summary: 'Owning a hospital is a dream for many doctors, but achieving this dream requires careful financial planning. Choosing the right funding options is crucial for the successful completion and operation of a hospital.',
    link: '#',
  },
  {
    title: 'Funding Options for New Hospital Projects',
    summary: 'The dream of many medical practitioners is to build or own a hospital. Achieving this dream requires meticulous planning and execution to avoid delays and cost escalations. Financial closure is a critical aspect of executing the project on time and running the hospital smoothly.',
    link: '#',
  },
  {
    title: 'Transforming Healthcare Dreams into Reality: Unveiling the Power of Hospital Project Financing',
    summary: 'The dream of every medical practitioner is to build or own a hospital. Building a hospital requires meticulous planning and execution to avoid delays and cost escalations. Most medical professionals depend on known sources instead of approaching professional consultants.',
    link: '#',
  },
];

const faqs = [
  { q: 'What is the applicable interest rate for Project Finance?', a: 'Interest rates start from 9%* onwards.' },
  { q: "What is the Minimum promoter's equity contribution to the project?", a: "This depends on the lender and project profile. Please fill the form for personalized options." },
  { q: 'What is the maximum loan tenor and moratorium?', a: 'Tenor and moratorium are customized based on your project and lender.' },
  { q: 'How long does it typically take to process the loan?', a: '96% of proposals are sanctioned within 45 days. Approval letter is typically received within 30 days of document submission.' },
  { q: 'What is the service fee you charge for securing our loan?', a: 'Service fees are transparent and will be discussed during the process.' },
];

const testimonials = [
  {
    quote: 'Avalue Capital helped us achieve financial closure for our 150-bed multi-specialty hospital on time. Their team coordinated with multiple lenders and got us a blended interest rate that worked for our projections.',
    name: 'Dr. Ramesh Iyer',
    title: 'Promoter, Sunrise Hospitals',
  },
  {
    quote: 'Our greenfield hospital project had a complex structure, but the experts simplified the process and supported us from proposal to disbursal.',
    name: 'Dr. Kavita Menon',
    title: 'Founder, CareFirst Healthcare',
  },
];

const SectionDivider = () => (
  <div className="w-full flex justify-center">
    <svg height="8" width="100%" className="animate-pulse">
      <defs>
        <linearGradient id="lineGradProj" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#66a9d6" />
          <stop offset="100%" stopColor="#e63946" />
        </linearGradient>
      </defs>
      <rect width="100%" height="8" rx="4" fill="url(#lineGradProj)" />
    </svg>
  </div>
);

const ProjectLoansPage = () => {
  const [form, setForm] = useState({
    company: '', contact: '', mobile: '', email: '', type: '', beds: '', premises: ''
  });
  const [faqOpen, setFaqOpen] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      const result = await emailService.sendProjectLoanEmail(form);
      
      if (result.success) {
        setSubmitMessage('Application submitted successfully! We will contact you soon.');
        setForm({ company: '', contact: '', mobile: '', email: '', type: '', beds: '', premises: '' }); // Reset form
      } else {
        setSubmitMessage('Failed to submit application. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-100 via-white to-red-100 overflow-x-hidden">
      {/* Hero Section */}
      <section className="w-full relative flex flex-col md:flex-row items-center justify-between px-4 md:px-0 py-12 md:py-20 bg-gradient-to-br from-blue-200 via-white to-red-100 overflow-hidden">
        {/* Decorative SVG */}
        <svg className="absolute top-0 left-0 w-48 h-48 opacity-10 -z-10" viewBox="0 0 100 100"><ellipse cx="50" cy="50" rx="40" ry="20" fill="#66a9d6" /></svg>
        <div className="flex-1 flex flex-col items-start justify-center z-10 max-w-2xl w-full mb-12 md:mb-0 px-2 md:px-16">
          <h1 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-4 leading-tight">Build Your Dream Hospital with <span className="text-red-500">Confidence</span></h1>
          <p className="text-lg md:text-xl text-gray-700 mb-6">Your dream hospital deserves timely support — and with Avalue Capital, it will receive just that. We specialize in healthcare project finance, assisting medical professionals in securing optimal terms, minimal collateral, and expedited approvals. Our dedicated team manages the financial process, allowing you to concentrate on delivering quality healthcare.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 w-full">
            <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center">
              <span className="text-xl font-bold text-blue-700">9%*</span>
              <span className="text-xs text-gray-500">Interest Rate onwards</span>
            </div>
            <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center">
              <span className="text-xl font-bold text-blue-700">Minimal</span>
              <span className="text-xs text-gray-500">Collateral</span>
            </div>
            <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center">
              <span className="text-xl font-bold text-blue-700">Custom</span>
              <span className="text-xs text-gray-500">Repayment</span>
            </div>
          </div>
        </div>
        {/* Form Card */}
        <div className="flex-1 max-w-md w-full bg-white/90 backdrop-blur rounded-2xl shadow-2xl p-8 md:mr-16 z-10">
          <h2 className="text-2xl font-bold mb-2 text-blue-700">Free Project Report — Start Your Hospital Journey Today</h2>
          <p className="text-sm text-gray-500 mb-4">Fill out the form below to get expert financial support for your project.</p>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input name="company" required placeholder="Name of the Company" className="w-full border rounded px-3 py-2" value={form.company} onChange={handleChange} />
            <input name="contact" required placeholder="Contact Person Name" className="w-full border rounded px-3 py-2" value={form.contact} onChange={handleChange} />
            <input name="mobile" required placeholder="Mobile No" className="w-full border rounded px-3 py-2" value={form.mobile} onChange={handleChange} />
            <input name="email" type="email" required placeholder="E-Mail ID" className="w-full border rounded px-3 py-2" value={form.email} onChange={handleChange} />
            <select name="type" required className="w-full border rounded px-3 py-2" value={form.type} onChange={handleChange}>
              <option value="">Type of Hospital</option>
              <option>Hospital</option>
              <option>Infrastructure</option>
              <option>Other</option>
            </select>
            <select name="beds" required className="w-full border rounded px-3 py-2" value={form.beds} onChange={handleChange}>
              <option value="">No. of Beds</option>
              <option>0-25</option>
              <option>25-50</option>
              <option>50-100</option>
              <option>100-200</option>
              <option>200+</option>
            </select>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input type="radio" name="premises" value="Owned" checked={form.premises === 'Owned'} onChange={handleChange} className="mr-2" /> Owned
              </label>
              <label className="flex items-center">
                <input type="radio" name="premises" value="Rented" checked={form.premises === 'Rented'} onChange={handleChange} className="mr-2" /> Rented
              </label>
            </div>
            <button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-red-500 hover:from-blue-600 hover:to-red-600 text-white font-semibold py-2 rounded shadow-lg transition" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            {submitMessage && (
              <div className={`text-center font-semibold mt-2 ${submitMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}>
                {submitMessage}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">Furnish the required details and click Submit for expedited loan processing.</p>
          </form>
        </div>
      </section>

      <SectionDivider />

      {/* Steps Section */}
      <section className="w-full relative py-12 bg-gradient-to-r from-blue-50 via-white to-blue-100 overflow-hidden">
        <svg className="absolute right-0 top-0 w-32 h-32 opacity-10 animate-spin-slow" viewBox="0 0 100 100"><rect x="20" y="20" width="60" height="60" rx="20" fill="#66a9d6" /></svg>
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center relative z-10">How to Get Your Loan Sanctioned</h3>
        <div className="relative flex flex-col md:flex-row md:justify-between md:items-start w-full gap-12 md:gap-0 px-0 max-w-5xl mx-auto z-10">
          {/* Step 1 */}
          <div className="relative flex-1 flex flex-col items-center text-center z-10">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white bg-blue-500 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border-4 border-white mb-2">1</span>
              <span className="text-4xl md:text-5xl mb-2">📝</span>
            </div>
            <h4 className="font-semibold mt-2 mb-1 text-lg text-blue-800">Fill the form</h4>
            <p className="text-gray-600 max-w-xs">Provide the information required in adjacent form to assess your requirement.</p>
          </div>
          {/* Connector 1->2 */}
          <div className="hidden md:block absolute left-[18%] top-10 w-[22%] h-24 z-0 pointer-events-none">
            <svg width="100%" height="100%" viewBox="0 0 180 80" fill="none">
              <path d="M10 20 Q90 70 170 20" stroke="#66a9d6" strokeWidth="4" fill="none" />
            </svg>
          </div>
          {/* Step 2 */}
          <div className="relative flex-1 flex flex-col items-center text-center z-10 md:mt-16">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white bg-green-500 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border-4 border-white mb-2">2</span>
              <span className="text-4xl md:text-5xl mb-2">📑</span>
            </div>
            <h4 className="font-semibold mt-2 mb-1 text-lg text-green-800">Build Project Report</h4>
            <p className="text-gray-600 max-w-xs">Our Financial Experts will meet / arrange a call to discuss and build Detailed Project Report</p>
          </div>
          {/* Connector 2->3 */}
          <div className="hidden md:block absolute left-[58%] top-28 w-[22%] h-24 z-0 pointer-events-none">
            <svg width="100%" height="100%" viewBox="0 0 180 80" fill="none">
              <path d="M10 60 Q90 10 170 60" stroke="#e63946" strokeWidth="4" fill="none" />
            </svg>
          </div>
          {/* Step 3 */}
          <div className="relative flex-1 flex flex-col items-center text-center z-10 md:mt-32">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold text-white bg-red-500 rounded-full w-12 h-12 flex items-center justify-center shadow-lg border-4 border-white mb-2">3</span>
              <span className="text-4xl md:text-5xl mb-2">✅</span>
            </div>
            <h4 className="font-semibold mt-2 mb-1 text-lg text-red-800">Get the sanction</h4>
            <p className="text-gray-600 max-w-xs">Receive the approval letter within 30 days of submission of Documents with Lenders</p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Advantage Section */}
      <section className="w-full py-12 bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center">Advantage with Avalue Capital</h3>
        <div className="flex flex-wrap justify-center gap-8 max-w-5xl mx-auto px-4">
          <div className="flex flex-col items-center bg-white rounded-2xl shadow-lg p-8 border border-blue-100 w-64">
            <span className="text-3xl mb-2">🏥</span>
            <div className="font-semibold text-blue-800 mb-1">Healthcare Expertise</div>
            <div className="text-gray-600 text-sm">15+ Lenders</div>
          </div>
          <div className="flex flex-col items-center bg-white rounded-2xl shadow-lg p-8 border border-blue-100 w-64">
            <span className="text-3xl mb-2">🤝</span>
            <div className="font-semibold text-blue-800 mb-1">Loan Syndication</div>
            <div className="text-gray-600 text-sm">Best Terms</div>
          </div>
          <div className="flex flex-col items-center bg-white rounded-2xl shadow-lg p-8 border border-blue-100 w-64">
            <span className="text-3xl mb-2">🛡️</span>
            <div className="font-semibold text-blue-800 mb-1">End to End Hand Holding</div>
            <div className="text-gray-600 text-sm">96% of proposals sanctioned within 45 days</div>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Resources Section */}
      <section className="w-full py-12 bg-gradient-to-r from-blue-50 via-white to-red-50">
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center">Insightful Resources to Empower Your Healthcare Project</h3>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto px-4">
          {resources.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl shadow p-6 flex flex-col border border-blue-100">
              <div className="font-semibold text-blue-800 mb-2">{r.title}</div>
              <div className="text-gray-600 text-sm mb-4">{r.summary}</div>
              <a href={r.link} className="text-blue-500 underline mt-auto">Read More</a>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="w-full py-12 bg-gradient-to-r from-blue-50 via-white to-red-50">
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center">Promoter Stories</h3>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto px-4">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl shadow p-8 flex flex-col border border-blue-100 items-center text-center">
              <svg className="w-10 h-10 mb-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 17a4 4 0 01-4-4V7a4 4 0 014-4h2a4 4 0 014 4v6a4 4 0 01-4 4zm10 0a4 4 0 01-4-4V7a4 4 0 014-4h2a4 4 0 014 4v6a4 4 0 01-4 4z" />
              </svg>
              <div className="text-gray-700 text-lg mb-4 italic">"{t.quote}"</div>
              <div className="font-bold text-blue-800">{t.name}</div>
              <div className="text-sm text-gray-500">{t.title}</div>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider />

      {/* FAQ Section */}
      <section className="w-full py-12 bg-gradient-to-br from-blue-100 via-white to-blue-50">
        <h3 className="text-3xl font-bold text-blue-700 mb-10 text-center">Answers to your questions</h3>
        <div className="space-y-4 max-w-3xl mx-auto px-4">
          {faqs.map((faq, i) => (
            <div key={i} className="border rounded-lg bg-white shadow-sm">
              <button type="button" className="w-full text-left px-4 py-3 font-semibold text-blue-800 focus:outline-none flex justify-between items-center" onClick={() => setFaqOpen(faqOpen === i ? null : i)}>
                {faq.q}
                <span>{faqOpen === i ? '-' : '+'}</span>
              </button>
              {faqOpen === i && (
                <div className="px-4 pb-4 text-gray-700 text-sm">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ProjectLoansPage; 